import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'
import { startOfMonth, endOfMonth } from 'date-fns'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { ReturnModal } from '@components/modals/ReturnModal'
import { emotionTheme } from '@styles/theme'
import { DatePicker } from '@components/shared/DatePicker'
import { Input } from '@components/shared/Input'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { currencyMask } from '@utils/masks/currencyMask'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import { convertDateToIso, formatDateForDisplay } from '@pages/People/components/modals/RescissionSimulationModal/helpers/dateConverter'
import { useUser } from '@hooks/useUser'
import { parseCompetenceDate } from '@utils/dates/parseCompetenceDate'
import * as apiPeople from '@services/api/people'

import type { EditAdmissionSimulationModalProps } from './types'
import { editAdmissionSimulationSchema } from './form/validations'
import { initialFormValues } from './form/initialFormValues'
import type { EditAdmissionSimulationFormProps } from './form/types'

import * as S from './styles'

export const EditAdmissionSimulationModal = ({
  open,
  onClose,
  simulation,
  onConfirm,
  isLoading = false,
  canEdit = true
}: EditAdmissionSimulationModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { filiData } = useUser()
  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [proportionalSalary, setProportionalSalary] = useState<number | null>(null)
  const [isCalculatingSalary, setIsCalculatingSalary] = useState(false)

  const methods = useForm<EditAdmissionSimulationFormProps>({
    resolver: editAdmissionSimulationSchema,
    defaultValues: initialFormValues
  })

  const { isDirty } = methods.formState
  const formRef = useRef<HTMLFormElement>(null)

  const salary = methods.watch('salary')
  const startDate = methods.watch('startDate')

  // Pré-popular formulário quando modal abre
  useEffect(() => {
    if (!open || !simulation) {
      methods.reset(initialFormValues)
      setProportionalSalary(null)
      return
    }

    const startDateForForm = simulation.startDate
      ? formatDateForDisplay(simulation.startDate)
      : ''

    methods.reset({
      position: simulation.position || '',
      startDate: startDateForForm,
      salary: simulation.salary ? currencyMask(simulation.salary.toFixed(2)) : ''
    })

    setProportionalSalary(simulation.proportionalSalary ?? null)
  }, [open, simulation, methods])

  // Recalcular salário proporcional ao alterar salário ou data de início
  useEffect(() => {
    if (!salary || !startDate || !filiData?.filiCode) {
      setProportionalSalary(null)
      return
    }

    const salaryValue = unMaskCurrency(salary)
    if (!salaryValue || salaryValue <= 0) {
      setProportionalSalary(null)
      return
    }

    const startDateIso = convertDateToIso(startDate)
    if (!startDateIso) {
      setProportionalSalary(null)
      return
    }

    setIsCalculatingSalary(true)
    apiPeople.calculateProportionalSalaryAdmission(filiData.filiCode, { salary: salaryValue, startDate: startDateIso })
      .then((response) => {
        setProportionalSalary(response.proportionalSalary)
      })
      .catch(() => {
        setProportionalSalary(null)
      })
      .finally(() => {
        setIsCalculatingSalary(false)
      })
  }, [salary, startDate, filiData?.filiCode])

  const onSubmit = methods.handleSubmit(async (formData) => {
    if (!canEdit) return
    if (!simulation) return

    // Lê o salário do DOM no momento do submit para evitar valor desatualizado (digitação ou deleção)
    const salaryInput = formRef.current?.elements?.namedItem?.('salary') as HTMLInputElement | null
    const salaryRaw = salaryInput?.value ?? formData.salary
    const salaryValue = salaryRaw ? unMaskCurrency(salaryRaw) : 0

    await onConfirm({
      position: formData.position,
      startDate: convertDateToIso(formData.startDate),
      salary: salaryValue
    })

    methods.reset(initialFormValues)
    onClose(false)
  })

  const handleClose = (value: boolean) => {
    if (isDirty && !value) {
      setOpenCancelModal(true)
    } else {
      methods.reset(initialFormValues)
      onClose(value)
    }
  }

  const referenceMonthRange = useMemo(() => {
    if (!filiData?.startPeriod) return null
    const refDate = parseCompetenceDate(filiData.startPeriod)
    if (!refDate) return null
    return {
      min: startOfMonth(refDate),
      max: endOfMonth(refDate),
      openTo: startOfMonth(refDate)
    }
  }, [filiData?.startPeriod])

  if (!simulation) return null

  return (
    <>
      <BaseModal2
        open={open}
        title="Editar Simulação de Admissão"
        headerIcon={<WarningIcon />}
        onClose={handleClose}
        width={isMobile ? 'calc(100% - 20px)' : 600}
        footer={{
          cancelButtonText: 'Cancelar',
          confirmButtonText: canEdit ? 'Salvar' : undefined,
          loading: isLoading,
          onConfirm: canEdit ? onSubmit : undefined,
          cancelButtonMinWidth: 'calc(50% - 8px)',
          confirmButtonMinWidth: 'calc(50% - 8px)'
        }}
      >
        <FormProvider {...methods}>
          <S.Form ref={formRef}>
            <S.FieldsContainer>
              <Input
                name="position"
                label="Cargo"
                placeholder="Informe o cargo"
                required
                disabled={!canEdit}
              />

              <DatePicker
                name="startDate"
                label="Data de Admissão"
                placeholder="Selecione a data"
                required
                minDate={referenceMonthRange?.min}
                maxDate={referenceMonthRange?.max}
                openToDate={referenceMonthRange?.openTo}
                disabled={!canEdit}
              />

              <Input
                name="salary"
                label="Salário"
                placeholder="R$ 0,00"
                maskType="currency"
                required
                disabled={!canEdit}
              />

              <S.ProportionalSalaryContainer>
                <Input
                  hasController={false}
                  name="proportionalSalary"
                  label="Salário Proporcional"
                  value={
                    isCalculatingSalary
                      ? '...'
                      : proportionalSalary !== null
                        ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(proportionalSalary)
                        : 'Será calculado automaticamente'
                  }
                  disabled
                  tooltipInfo="Cálculo: Salário base ÷ 30 × quantidade de dias restantes no mês."
                />
              </S.ProportionalSalaryContainer>
            </S.FieldsContainer>
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a edição?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelModal}
        onConfirm={() => {
          methods.reset(initialFormValues)
          onClose(false)
          setOpenCancelModal(false)
        }}
      />
    </>
  )
}
